import { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import { he } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './PublicDonation.module.css';
import { submitOfflineDonation, fetchBanks, BankDto } from './api';
import { DonationFormState } from './types';
import { DESTINATION_BANK_ACCOUNTS, DestinationBankAccount } from './destinationBankAccounts';
import CameraCapture from './CameraCapture';

registerLocale('he', he);

type CheckSubType = 'ForDeposit' | 'Deposited';

interface Props {
  fundId: number;
  form: DonationFormState;
  type: 'Check' | 'BankTransfer';
  onSuccess: () => void;
  onError: (message: string) => void;
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function useFilePreviewUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const prevFileRef = useRef<File | null>(null);

  if (file !== prevFileRef.current) {
    if (url) URL.revokeObjectURL(url);
    prevFileRef.current = file;
    if (file) {
      const newUrl = URL.createObjectURL(file);
      setUrl(newUrl);
      return newUrl;
    }
    setUrl(null);
    return null;
  }
  return url;
}

interface FilePickerProps {
  label: string;
  file: File | null;
  onFile: (file: File | null) => void;
  disabled: boolean;
  allowCamera?: boolean;
}

const FilePicker = ({ label, file, onFile, disabled, allowCamera = false }: FilePickerProps) => {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [showCamera, setShowCamera] = useState(false);
  const previewUrl = useFilePreviewUrl(file);

  return (
    <div className={styles.field}>
      <label>{label}</label>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => onFile(e.target.files?.[0] ?? null)} disabled={disabled} />

      {previewUrl ? (
        <div className={styles.previewContainer}>
          <img src={previewUrl} alt={label} className={styles.previewImage} />
          <div className={styles.previewActions}>
            <span className={styles.fileName}>{file?.name}</span>
            <button type="button" className={styles.previewRemoveBtn} onClick={() => onFile(null)} disabled={disabled}>✕</button>
          </div>
        </div>
      ) : (
        <div className={styles.filePickerRow}>
          <button type="button" className={styles.filePickerBtn} onClick={() => fileRef.current?.click()} disabled={disabled}>
            📁 {t('donations.offline.uploadFile')}
          </button>
          {allowCamera && (
            <button type="button" className={styles.filePickerBtn} onClick={() => setShowCamera(true)} disabled={disabled}>
              📷 {t('donations.offline.takePhoto')}
            </button>
          )}
        </div>
      )}

      {showCamera && (
        <CameraCapture
          onCapture={(f) => { onFile(f); setShowCamera(false); }}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

const OfflineDonationStep = ({ fundId, form, type, onSuccess, onError }: Props) => {
  const { t, i18n } = useTranslation();

  const [checkNumber, setCheckNumber] = useState('');
  const [bankNumber, setBankNumber] = useState('');
  const [branchNumber, setBranchNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [checkSubType, setCheckSubType] = useState<CheckSubType>('ForDeposit');
  const [selectedDestBank, setSelectedDestBank] = useState<DestinationBankAccount | null>(null);
  const [proof, setProof] = useState<File | null>(null);
  const [secondProof, setSecondProof] = useState<File | null>(null);
  const [maturityType, setMaturityType] = useState<'immediate' | 'future'>('immediate');
  const [maturityDate, setMaturityDate] = useState<Date | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [banksMap, setBanksMap] = useState<Record<string, BankDto>>({});

  const isCheck = type === 'Check';
  const isDeposited = isCheck && checkSubType === 'Deposited';
  const needsDestinationBank = type === 'BankTransfer' || isDeposited;
  const needsProofFiles = !isDeposited;

  useEffect(() => {
    fetchBanks().then(setBanksMap).catch(() => {});
  }, []);

  const selectedBank = banksMap[bankNumber];
  const branches = useMemo(() => selectedBank?.branches ?? [], [selectedBank]);

  const clearError = (key: string) => setFieldErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!bankNumber) errors.bankNumber = t('donations.validation.required');
    if (!branchNumber) errors.branchNumber = t('donations.validation.required');

    const account = accountNumber.trim();
    if (!account) errors.accountNumber = t('donations.validation.required');
    else if (!/^\d{6,13}$/.test(account)) errors.accountNumber = t('donations.offline.invalidAccountNumber');

    if (isCheck) {
      const check = checkNumber.trim();
      if (!check) errors.checkNumber = t('donations.validation.required');
      else if (!/^\d{1,8}$/.test(check)) errors.checkNumber = t('donations.offline.invalidCheckNumber');
    }

    if (needsDestinationBank && !selectedDestBank) {
      errors.destinationBankAccount = t('donations.validation.required');
    }

    if (needsProofFiles) {
      if (!proof) errors.proof = t('donations.proofRequired');
      if (isCheck && !secondProof) errors.secondProof = t('donations.proofRequired');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const proofBase64 = proof ? await fileToBase64(proof) : undefined;
      const secondProofBase64 = secondProof ? await fileToBase64(secondProof) : undefined;

      await submitOfflineDonation(fundId, {
        type,
        donorFirstName: form.donor.firstName,
        donorLastName: form.donor.lastName,
        donorIsraeliId: form.donor.israeliId,
        donorPhone: form.donor.phone,
        donorEmail: form.donor.email,
        donorAddress: form.donor.address,
        donorCity: form.donor.city,
        amount: form.amount,
        currency: form.currency,
        checkNumber: isCheck ? checkNumber : undefined,
        bankNumber,
        branchNumber,
        accountNumber,
        checkSubType: isCheck ? checkSubType : undefined,
        destBankNumber: needsDestinationBank ? selectedDestBank?.bankNumber : undefined,
        destBranchNumber: needsDestinationBank ? selectedDestBank?.branchNumber : undefined,
        destAccountNumber: needsDestinationBank ? selectedDestBank?.accountNumber : undefined,
        proofFileName: proof?.name,
        proofFileBase64: proofBase64,
        secondProofFileName: secondProof?.name,
        secondProofFileBase64: secondProofBase64,
        maturityDate: isCheck && maturityType === 'future' && maturityDate
          ? maturityDate.toISOString().split('T')[0]
          : undefined,
      });

      onSuccess();
    } catch {
      onError(t('donations.submitOfflineError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.section} style={{ gap: 48 }}>
      <p className={styles.requiredNote}>{t('donations.requiredFieldsNote')}</p>

      {isCheck && (
        <div className={styles.section} style={{ gap: 14 }}>
          <label className={styles.sectionTitle}>{t('donations.offline.checkSubType')}</label>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input
                type="radio"
                checked={checkSubType === 'ForDeposit'}
                onChange={() => { setCheckSubType('ForDeposit'); setSelectedDestBank(null); clearError('destinationBankAccount'); }}
                disabled={submitting}
              />
              {t('donations.offline.checkForDeposit')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input
                type="radio"
                checked={checkSubType === 'Deposited'}
                onChange={() => { setCheckSubType('Deposited'); setProof(null); setSecondProof(null); clearError('proof'); clearError('secondProof'); }}
                disabled={submitting}
              />
              {t('donations.offline.checkDeposited')}
            </label>
          </div>
        </div>
      )}

      <div className={styles.fieldsGrid}>
        {isCheck && (
          <div className={styles.field}>
            <label>{t('donations.offline.checkNumber')} <span className={styles.requiredMark}>{'*'}</span></label>
            <input
              className={fieldErrors.checkNumber ? styles.fieldInputError : ''}
              value={checkNumber}
              onChange={(e) => { setCheckNumber(e.target.value); clearError('checkNumber'); }}
              inputMode="numeric"
              maxLength={8}
              disabled={submitting}
            />
            {fieldErrors.checkNumber && <small className={styles.fieldError}>{fieldErrors.checkNumber}</small>}
          </div>
        )}
        <div className={styles.field}>
          <label>{t('donations.offline.bankNumber')} <span className={styles.requiredMark}>{'*'}</span></label>
          <select
            className={fieldErrors.bankNumber ? styles.fieldInputError : ''}
            value={bankNumber}
            onChange={(e) => { setBankNumber(e.target.value); setBranchNumber(''); clearError('bankNumber'); }}
            disabled={submitting}
          >
            <option value="">{t('donations.offline.selectBank')}</option>
            {Object.entries(banksMap).map(([id, bank]) => (
              <option key={id} value={id}>{id} - {bank.name}</option>
            ))}
          </select>
          {fieldErrors.bankNumber && <small className={styles.fieldError}>{fieldErrors.bankNumber}</small>}
        </div>
        <div className={styles.field}>
          <label>{t('donations.offline.branchNumber')} <span className={styles.requiredMark}>{'*'}</span></label>
          <select
            className={fieldErrors.branchNumber ? styles.fieldInputError : ''}
            value={branchNumber}
            onChange={(e) => { setBranchNumber(e.target.value); clearError('branchNumber'); }}
            disabled={submitting || !bankNumber}
          >
            <option value="">{t('donations.offline.selectBranch')}</option>
            {branches.map((b) => (
              <option key={b.id} value={String(b.id)}>{b.id} - {b.name}</option>
            ))}
          </select>
          {fieldErrors.branchNumber && <small className={styles.fieldError}>{fieldErrors.branchNumber}</small>}
        </div>
        <div className={styles.field}>
          <label>{t('donations.offline.accountNumber')} <span className={styles.requiredMark}>{'*'}</span></label>
          <input
            className={fieldErrors.accountNumber ? styles.fieldInputError : ''}
            value={accountNumber}
            onChange={(e) => { setAccountNumber(e.target.value); clearError('accountNumber'); }}
            inputMode="numeric"
            maxLength={13}
            disabled={submitting}
          />
          {fieldErrors.accountNumber && <small className={styles.fieldError}>{fieldErrors.accountNumber}</small>}
        </div>
      </div>

      {needsDestinationBank && (
        <>
          <div className={styles.divider} />
          <div className={styles.section} style={{ gap: 14 }}>
            <label className={styles.sectionTitle}>
              {t('donations.offline.destinationBankAccount')} <span className={styles.requiredMark}>{'*'}</span>
            </label>
            <div className={styles.field}>
              <select
                className={fieldErrors.destinationBankAccount ? styles.fieldInputError : ''}
                value={selectedDestBank?.id ?? ''}
                onChange={(e) => { setSelectedDestBank(DESTINATION_BANK_ACCOUNTS.find((a) => a.id === e.target.value) ?? null); clearError('destinationBankAccount'); }}
                disabled={submitting}
              >
                <option value="">{t('donations.offline.selectDestinationBank')}</option>
                {DESTINATION_BANK_ACCOUNTS.map((acc) => (
                  <option key={acc.id} value={acc.id}>{acc.label}</option>
                ))}
              </select>
              {fieldErrors.destinationBankAccount && <small className={styles.fieldError}>{fieldErrors.destinationBankAccount}</small>}
            </div>
          </div>
        </>
      )}

      {isCheck && (
        <>
          <div className={styles.divider} />
          <div className={styles.section} style={{ gap: 14 }}>
            <label className={styles.sectionTitle}>{t('donations.offline.maturityDate')}</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="radio" checked={maturityType === 'immediate'} onChange={() => { setMaturityType('immediate'); setMaturityDate(null); }} disabled={submitting} />
                {t('donations.offline.maturityImmediate')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="radio" checked={maturityType === 'future'} onChange={() => setMaturityType('future')} disabled={submitting} />
                {t('donations.offline.maturityFuture')}
              </label>
            </div>
            {maturityType === 'future' && (
              <DatePicker
                selected={maturityDate}
                onChange={(date) => setMaturityDate(date)}
                dateFormat="dd/MM/yyyy"
                locale={i18n.language}
                minDate={new Date()}
                disabled={submitting}
                placeholderText={t('donations.offline.selectDate')}
                className={styles.dateInput}
                popperPlacement="bottom-start"
              />
            )}
          </div>
        </>
      )}

      {needsProofFiles && (
        <>
          <div className={styles.divider} />

          <div className={styles.section} style={{ gap: 14 }}>
            <label className={styles.sectionTitle}>
              {isCheck ? t('donations.offline.checkPhotos') : t('donations.offline.proofPhoto')}
            </label>
            {isCheck && (
              <small className={styles.photoWarning}>{t('donations.offline.clearPhotoWarning')}</small>
            )}
            <div className={styles.filePickerGrid}>
              <div className={`${styles.filePickerCard} ${fieldErrors.proof ? styles.filePickerCardError : ''}`}>
                <FilePicker
                  label={isCheck ? t('donations.checkFront') : t('donations.proofFile')}
                  file={proof}
                  onFile={(f) => { setProof(f); clearError('proof'); }}
                  disabled={submitting}
                  allowCamera={isCheck}
                />
                {fieldErrors.proof && <small className={styles.fieldError} style={{ position: 'static' }}>{fieldErrors.proof}</small>}
              </div>
              {isCheck && (
                <div className={`${styles.filePickerCard} ${fieldErrors.secondProof ? styles.filePickerCardError : ''}`}>
                  <FilePicker
                    label={t('donations.checkBack')}
                    file={secondProof}
                    onFile={(f) => { setSecondProof(f); clearError('secondProof'); }}
                    disabled={submitting}
                    allowCamera
                  />
                  {fieldErrors.secondProof && <small className={styles.fieldError} style={{ position: 'static' }}>{fieldErrors.secondProof}</small>}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <button type="button" className={styles.submitBtn} onClick={submit} disabled={submitting}>
        {submitting ? t('sending') : t('donations.submitOffline')}
      </button>
    </div>
  );
};

export default OfflineDonationStep;
